import { useState, useEffect } from 'react';
import { isEmpty, filter as filterFunction, find } from 'lodash';
import { useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import css from 'styled-jsx/css';
import ScrollingContainer from 'components/ScrollingContainer';
import Header from 'components/MakeModelMenu/Header';
import ModelsList from 'components/MakeModelMenu/ModelsList';
import MakesList from 'components/MakeModelMenu/MakesList';
import { screen, colors } from 'components/styles/variables';
import { useMeasure, useWindowSize } from 'react-use';
import State from 'types/State';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import Model from 'types/Model';
import Make from 'types/Make';
import { ChooseFunction, FilterFunction } from './types';

type DisabledProp = (model: Model) => boolean;

interface Props {
  allMakesAndModelsOption?: boolean;
  onSelect: ChooseFunction;
  filter?: FilterFunction;
  disabled?: DisabledProp;
}

export default function MakeModelMenu({
  onSelect,
  allMakesAndModelsOption,
  filter,
  disabled,
}: Props): JSX.Element {
  const [selectedMake, setSelectedMake] = useState<Make>();
  let selectedMakeName: string | undefined;
  const [headerRef, { height: headerHeight }] = useMeasure<HTMLDivElement>();
  const { height: windowHeight } = useWindowSize();
  const makesAndModels = useSelector<State, Make[]>((state) => state.makesAndModels);
  const { isMobile } = useDeviceScreen();

  const scrollingContainerHeight = isMobile ? windowHeight - headerHeight : undefined;

  useEffect(() => {
    try {
      if (selectedMakeName) {
        const newSelectedMake = find(makesAndModels, {
          name: selectedMakeName,
        });
        setSelectedMake(newSelectedMake);
      }
    } catch (response) {
      console.error(`error fetching makes: ${response}`);
    }
  }, [selectedMakeName, makesAndModels]);

  const scoped = css.resolve`
    .loading-container {
      margin: 25px auto;
    }

    @media (min-width: ${screen.smMin}) {
      .mm-scrolling-container {
        height: calc(70vh - 60px);
        max-height: 600px;
        min-height: 200px;
      }
    }
  `;

  return (
    <div data-make-model-menu>
      <Header
        hasSelectedMake={!!selectedMake}
        showMakes={(): void => {
          setSelectedMake(undefined);
        }}
        ref={headerRef}
      />
      <div className="body">
        {isEmpty(makesAndModels) ? (
          <ReactLoading
            type="spokes"
            color={colors.BLACK}
            width={40}
            height={40}
            className={`loading-container ${scoped.className}`}
          />
        ) : (
          <ScrollingContainer
            className={`${scoped.className} mm-scrolling-container`}
            height={scrollingContainerHeight}
          >
            <MakesList
              allMakesAndModelsOption={allMakesAndModelsOption}
              makes={makesAndModels}
              choose={setSelectedMake}
              chooseAll={onSelect}
              className={`${scoped.className} ${selectedMake && 'hidden'}`}
            />
            {selectedMake && (
              <ModelsList
                // Filter models by a function passed in as a prop,
                // if none was passed, this will return all models
                models={filterFunction(selectedMake.models, filter) as Model[]}
                make={selectedMake}
                disabled={disabled}
                choose={(model: Model): void => {
                  onSelect(selectedMake, model);
                }}
                allModelsOption={allMakesAndModelsOption}
                className={scoped.className}
              />
            )}
          </ScrollingContainer>
        )}
      </div>
      {scoped.styles}
      <style jsx>
        {`
          .body {
            overflow: hidden;
          }
        `}
      </style>
    </div>
  );
}
